import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { T, useGT } from 'gt-react';
import { parseDateOnly } from '@/utils/dateUtils';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { MultiSelect } from '@/components/common/MultiSelect';
import { DateRangePicker } from '@/components/common/date-pickers/date-range-picker';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import {
  isFieldActive,
  buildFieldClearPatch,
  type FilterFieldConfig,
  type FilterParams,
} from './filter-field-config';

const TOGGLE_ANY_VALUE = '__any__';

function fieldRowKey(field: FilterFieldConfig): string {
  switch (field.type) {
    case 'dateRange':
      return `${field.fromKey}-${field.toKey}`;
    case 'numberRange':
      return `${field.minKey}-${field.maxKey}`;
    default:
      return field.key;
  }
}

function FilterFieldControl({
  field,
  params,
  onChange,
}: {
  field: FilterFieldConfig;
  params: FilterParams;
  onChange: (patch: FilterParams) => void;
}) {
  const gt = useGT();

  switch (field.type) {
    case 'multiSelect': {
      const selectedIds = Array.isArray(params[field.key])
        ? (params[field.key] as string[])
        : [];
      return (
        <MultiSelect
          options={field.options}
          value={field.options.filter((o) => selectedIds.includes(o.id))}
          onChange={(selected) => {
            const ids = selected.map((o) => o.id);
            onChange({ [field.key]: ids.length > 0 ? ids : undefined });
          }}
          placeholder={field.placeholder}
          size="sm"
          shouldAutoSort={false}
        />
      );
    }
    case 'dateRange': {
      const fromStr = params[field.fromKey];
      const toStr = params[field.toKey];
      const range: DateRange | undefined =
        fromStr != null || toStr != null
          ? {
              from:
                typeof fromStr === 'string'
                  ? (parseDateOnly(fromStr) ?? undefined)
                  : undefined,
              to:
                typeof toStr === 'string'
                  ? (parseDateOnly(toStr) ?? undefined)
                  : undefined,
            }
          : undefined;
      return (
        <DateRangePicker
          value={range}
          onChange={(next) =>
            onChange({
              [field.fromKey]: next?.from
                ? format(next.from, 'yyyy-MM-dd')
                : undefined,
              [field.toKey]: next?.to
                ? format(next.to, 'yyyy-MM-dd')
                : undefined,
            })
          }
          placeholder={field.placeholder}
          className="w-full"
        />
      );
    }
    case 'numberRange': {
      const minVal = params[field.minKey];
      const maxVal = params[field.maxKey];
      return (
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder={gt('Min', {
              context: 'filter field number range min input',
            })}
            value={typeof minVal === 'number' ? minVal : ''}
            onChange={(e) =>
              onChange({
                [field.minKey]: e.target.value
                  ? Number(e.target.value)
                  : undefined,
              })
            }
            className="h-8"
          />
          <Input
            type="number"
            placeholder={gt('Max', {
              context: 'filter field number range max input',
            })}
            value={typeof maxVal === 'number' ? maxVal : ''}
            onChange={(e) =>
              onChange({
                [field.maxKey]: e.target.value
                  ? Number(e.target.value)
                  : undefined,
              })
            }
            className="h-8"
          />
        </div>
      );
    }
    case 'toggleGroup': {
      const current =
        typeof params[field.key] === 'string'
          ? (params[field.key] as string)
          : TOGGLE_ANY_VALUE;
      return (
        <ToggleGroup
          type="single"
          size="sm"
          value={current}
          onValueChange={(value) => {
            if (!value) return;
            onChange({
              [field.key]: value === TOGGLE_ANY_VALUE ? undefined : value,
            });
          }}
          className="flex gap-1"
        >
          <ToggleGroupItem value={TOGGLE_ANY_VALUE} className="text-xs flex-1">
            {field.anyLabel}
          </ToggleGroupItem>
          {field.options.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value}
              className="text-xs flex-1"
            >
              {option.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      );
    }
    case 'customer': {
      const selected = Array.isArray(params[field.key])
        ? (params[field.key] as string[])[0]
        : undefined;
      return (
        <CustomersAutocomplete
          selectedCustomerId={selected}
          onCustomerSelect={(customerId) =>
            onChange({ [field.key]: customerId ? [customerId] : undefined })
          }
          allowClear
        />
      );
    }
  }
}

function FilterFieldRow({
  field,
  params,
  onChange,
}: {
  field: FilterFieldConfig;
  params: FilterParams;
  onChange: (patch: FilterParams) => void;
}) {
  const active = isFieldActive(field, params);

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <label className="text-sm font-medium">{field.label}</label>
        {active && (
          <button
            type="button"
            className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted-foreground/20"
            onClick={() => onChange(buildFieldClearPatch(field))}
          >
            <T>clear</T>
          </button>
        )}
      </div>
      <FilterFieldControl field={field} params={params} onChange={onChange} />
    </div>
  );
}

/**
 * Renders a config-driven list of filter fields (label + clear pill +
 * control per field), reusing the existing `MultiSelect`/`DateRangePicker`/
 * `ToggleGroup`/`CustomersAutocomplete` primitives. Intended to sit inside a
 * `FilterPopoverButton` via `ListFilterToolbar`.
 */
export function FilterFieldsPanel({
  fields,
  params,
  onChange,
}: {
  fields: FilterFieldConfig[];
  params: FilterParams;
  onChange: (patch: FilterParams) => void;
}) {
  return (
    <div className="space-y-3">
      {fields.map((field) => (
        <FilterFieldRow
          key={fieldRowKey(field)}
          field={field}
          params={params}
          onChange={onChange}
        />
      ))}
    </div>
  );
}
